import React, { useEffect, useState } from 'react';

import classNames from 'classnames';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card } from '@buildoutinc/blueprint-react/ui/Card';
import { get } from 'helpers/api';

import type { CardTheme } from './card_theme';
import type { LinkCardToolCall } from '../types';

// The record says where it lives rather than the card assuming it was fetched from there, so a
// listing can point at its project.
export interface LinkCardRecord {
  path: string;
  thumbnailUrl?: string | null;
}

interface Props<T extends LinkCardRecord> {
  children: (record: T) => React.ReactNode;
  path: string;
  theme: CardTheme;
}

const MAX_CARDS = 3;

export function getCardPaths(toolCalls: LinkCardToolCall[]) {
  // One turn can surface the same record more than once - dedupe
  const allPaths = toolCalls.reduce<string[]>((paths, toolCall) => paths.concat(toolCall.metadata.paths ?? []), []);

  // Each card makes a request, so cap the number of cards instead of rendering all
  return Array.from(new Set(allPaths)).slice(0, MAX_CARDS);
}

interface ToolCardProps {
  children: React.ReactNode;
  href: string;
  theme: CardTheme;
  thumbnailUrl?: string | null;
}

export function ToolCard({ children, href, theme, thumbnailUrl }: ToolCardProps) {
  return (
    <a className="ai-assistant-tool-card text-decoration-none text-reset" href={href}>
      <Card>
        <Card.Body className="d-flex align-items-center gap-2">
          {thumbnailUrl ? (
            <img alt="" className="ai-assistant-tool-card-tile flex-shrink-0 rounded" src={thumbnailUrl} />
          ) : (
            <span
              className={classNames(
                'ai-assistant-tool-card-tile d-inline-flex align-items-center justify-content-center',
                'flex-shrink-0 rounded',
                theme.tileClassName
              )}
            >
              <FontAwesomeIcon icon={theme.icon} />
            </span>
          )}
          <div className="flex-grow-1 text-truncate">{children}</div>
          <FontAwesomeIcon className="flex-shrink-0 text-muted fs-small" icon={faArrowUpRightFromSquare} />
        </Card.Body>
      </Card>
    </a>
  );
}

export function LinkCard<T extends LinkCardRecord>({ children, path, theme }: Props<T>) {
  const [record, setRecord] = useState<T | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setRecord(await get(path));
      } catch {
        // The reply already answered the question, so a card that never arrives is no loss.
      }
    })();
  }, [path]);

  if (!record) {
    return null;
  }

  return (
    <ToolCard href={record.path} theme={theme} thumbnailUrl={record.thumbnailUrl}>
      {children(record)}
    </ToolCard>
  );
}
